import _ from "lodash"
import i18n from "@/i18n"
import { fi, sv, enGB, type Locale } from "date-fns/locale"
import * as yup from "yup"
import type { FormikProps } from "formik"
import type { SurveyInterface, LanguageCode } from "@myeway/types"
import { useGetTemplateRolesQueryL } from "@api/templaterole-api"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { authSelector } from "@myeway/auth"

const TRANSLATION_CODES = ["en", "sv"]

/**
 * Returns applicable locale for current language
 *
 * @param {String} languageCode						Optional: language code for locale
 * @returns {Object}											date-fns locale object
 */
export const getDateLocale = (languageCode?: string) => {
	const dic: Record<string, Locale> = {
		fi,
		en: enGB,
		sv,
	}
	return dic[languageCode || i18n.language] ?? fi
}

// Only pick string attributes present in the reference object
const pickDeep = (obj: unknown, refObj: unknown, accPath: string[] = [], accObj = {}) => {
	if (typeof obj === "string") {
		_.set(accObj, accPath, obj)
		return
	} else if (obj === undefined) return
	for (const k in refObj as Record<string, unknown>) {
		const newPath = [...accPath, k]
		pickDeep((obj as Record<string, unknown>)?.[k], (refObj as Record<string, unknown>)[k], newPath, accObj)
	}
	return accObj
}

/**
 * Uses object `translations` path to translate object string paths.
 * Mutates object.
 *
 * @param obj 									Input object to be translated
 * @param languageCode 					Language code
 * @returns											Translated object
 */
const mergeTranslations = (obj: Record<string, unknown>, languageCode: LanguageCode) => {
	if (obj?.translations) {
		const source =
			obj.translations instanceof Map ? obj.translations.get(languageCode) : _.get(obj, ["translations", languageCode])

		// Retain default language values so that switching to default language does not break
		if (languageCode !== "fi") {
			if (!_.has(obj, ["translations", "fi"])) {
				_.set(obj, ["translations", "fi"], pickDeep(obj, source))
			}
		}
		_.mergeWith(obj, source, (objValue, srcValue) => {
			if (srcValue === "") {
				return objValue
			}
			// This if statement caused fields that are deep like objects or arrays to not be translated.
			// This occurred for example in surveys, where only the top level has the `translations` path
			// if (!srcValue || typeof srcValue !== "string") {
			//	return objValue
			// }
		})
	}
	return obj
}

/**
 * Traverses object and updates translations at every nesting level.
 * Mutates input object if it is a POJO, but not when it is a mongoose Document.
 *
 * @param dest									Destination object; modified object of this will be returned
 * @param obj										Object used in recursion; should be `dest` in the initial call
 * @param languageCode					Language code
 * @returns											Translated destination object
 */
const locNestedObject = <T>(obj: T, languageCode: LanguageCode): T => {
	if (!obj || typeof obj !== "object") {
		return obj
	}

	const keys = _.keys(obj)
	for (const k of keys) {
		if (k === "translations") {
			mergeTranslations(obj as Record<string, unknown>, languageCode)
		} else {
			const o = (obj as Record<string, unknown>)[k]
			locNestedObject(o, languageCode)
		}
	}

	return obj
}

/**
 * Updates localized object paths to use translations for the current language.
 * Default path values remain if no translation is found.
 *
 * Dependent on `translations` top-level path in MongoDB object.
 *
 * @param obj 								Object or document to be translated
 * @param languageCode 			Language code for translations
 * @returns											Translated object, will be a POJO and not a mongoose Document
 */
export const localizeObject = <T>(obj: T, languageCode?: LanguageCode): T => {
	if (!obj) {
		return obj
	}
	if (!languageCode) {
		languageCode = i18n.language as LanguageCode
	}

	const copy = _.cloneDeep(obj)
	const r = locNestedObject(copy, languageCode)
	return r
}

// Localize object alias
export const loc = <T>(obj: T, languageCode?: LanguageCode): T => localizeObject(obj, languageCode)

// Localize array; returns [] if arg is null
export const locList = (list: Array<unknown>, languageCode?: LanguageCode) =>
	(Array.isArray(list) ? list : []).map((o) => loc(o, languageCode))
// Localize object or array; returns null if arg is null
export const locData = <T = unknown>(data: T, languageCode?: LanguageCode) =>
	_.isArray(data) ? (locList(data, languageCode) as T) : (loc(data, languageCode) as T)

/**
 * Converts object path to `translations` subpath
 *
 * @param path 						Object path
 * @param lang 						Language code
 * @param useTopPath			Will use the top translation path instead of embedded translations.
 * @returns								Object path to `translations` equivalent
 */
export const lPath = (path: string, lang: string, useTopPath?: boolean) => {
	if (!lang) {
		throw "Second parameter `lang` required"
	}

	if (lang === "fi") {
		return path
	}

	if (useTopPath) {
		return `translations.${lang}.${path}`
	}

	const pathArray = _.toPath(path)
	if (_.isEmpty(pathArray)) {
		throw "Incorrect input path"
	}

	const destinationAttribute = _.last(pathArray)
	const basePath = pathArray.slice(0, pathArray.length - 1)
	const arr = [...basePath, "translations", lang, destinationAttribute]
	const dotNotation = arr.join(".")
	return dotNotation
}

/**
 * Mimics the function of `Formik.getFieldProps` and returns Formik-compatible input props,
 * but uses the input language to determine the formik state paths.
 *
 * @param formik 						Formik object
 * @param path 							`_.set` and `setFieldValue` compatible path; dot notation is allowed
 * @param lang 							Language code; controls path for setting and getting localized value
 * @returns									Input props
 */
export const getLangFieldProps = (
	formik: Partial<FormikProps<unknown>> & Pick<FormikProps<unknown>, "setFieldValue" | "setFieldTouched">,
	path: string,
	lang: string
) => {
	const { values, setFieldValue, setFieldTouched, errors, touched } = formik

	const name = lPath(path, lang)
	const value = _.get(values, lPath(path, lang)) ?? ""
	const onChange = (e: { target: { value: unknown } }) => setFieldValue(lPath(path, lang), e.target.value)
	const onBlur = () => setFieldTouched(lPath(path, lang))
	const isInvalid = _.get(touched, lPath(path, lang)) && _.get(errors, lPath(path, lang))

	return { name, value, onChange, onBlur, isInvalid }
}

/**
 * Returns a flat dictionary of survey translations keyed by non-translated strings
 *
 * @param survey 								Survey document or object
 * @param languageCode 					Language code, defaults to i18n.language
 * @returns											Dictionary of translations
 */
export const getSurveyTranslationDictionary = (survey: SurveyInterface, languageCode?: LanguageCode) => {
	const dic: {
		[key: string]: string
	} = {}

	if (!languageCode) {
		languageCode = i18n.language as LanguageCode
	}

	const translation = _.get(survey, ["translations", languageCode])
	if (!translation) {
		return dic
	}

	if (translation.name) {
		dic[survey.name ?? ""] = translation.name
	}
	if (translation.description) {
		dic[survey.description ?? ""] = translation.description
	}

	for (let i = 0; i < translation.questionGroups?.length; i++) {
		const transQg = _.get(translation, ["questionGroups", i])
		const qgMatch = _.get(survey, ["questionGroups", i])
		if (qgMatch && transQg) {
			const entries = _.entries(transQg)
			entries
				.filter(([, v]) => _.isString(v))
				.forEach(([k, v]) => {
					const key = k as "name" | "text" | "tag"
					const str = qgMatch[key]
					const translated = v
					if (str && translated) {
						_.set(dic, [str], translated)
					}
				})
		}
		for (let j = 0; j < transQg.questions?.length; j++) {
			const transQ = _.get(transQg, ["questions", j])
			const qMatch = _.get(qgMatch, ["questions", j])
			if (transQ && qMatch) {
				_.entries(transQ)
					.filter(([, v]) => typeof v === "string")
					.forEach(([k, v]) => {
						const key = k as "title" | "text" | "endText" | "tag"
						const str = qMatch[key]
						const translated = v
						if (str && translated) {
							_.set(dic, [str], translated)
						}
					})
			}
			for (let k = 0; k < transQ?.choices?.length; k++) {
				const transC = _.get(transQ, ["choices", k])
				const cMatch = _.get(qMatch, ["choices", k])
				if (cMatch) {
					_.entries(transC)
						.filter(([, v]) => typeof v === "string")
						.forEach(([k, v]) => {
							const key = k as "label"
							const str = cMatch[key]
							const translated = v
							if (str && translated) {
								_.set(dic, [str], translated)
							}
						})
				}
			}
			for (let k = 0; k < transQ?.freeFormFields?.length; k++) {
				const transF = _.get(transQ, ["freeFormFields", k])
				const fMatch = _.get(qMatch, ["freeFormFields", k])
				if (fMatch) {
					_.entries(transF)
						.filter(([, v]) => typeof v === "string")
						.forEach(([k, v]) => {
							const key = k as "name" | "text"
							const str = fMatch[key]
							const translated = v
							if (str && translated) {
								_.set(dic, [str], translated)
							}
						})
				}
			}
		}
	}

	for (let i = 0; i < translation.questions?.length; i++) {
		const transQ = _.get(translation, ["questions", i])
		const qMatch = _.get(survey, ["questions", i])
		if (transQ && qMatch) {
			_.entries(transQ)
				.filter(([, v]) => typeof v === "string")
				.forEach(([k, v]) => {
					const key = k as "title" | "text" | "endText" | "tag"
					const str = qMatch[key]
					const translated = v
					if (str && translated) {
						_.set(dic, [str], translated)
					}
				})
		}
		for (let j = 0; j < transQ?.choices?.length; j++) {
			const transC = _.get(transQ, ["choices", j])
			const cMatch = _.get(qMatch, ["choices", j])
			if (transC && cMatch) {
				_.entries(transC)
					.filter(([, v]) => _.isString(v))
					.forEach(([k, v]) => {
						const key = k as "label"
						const str = cMatch[key]
						const translated = v
						if (str && translated) {
							_.set(dic, [str], translated)
						}
					})
			}
		}
		for (let k = 0; k < transQ?.freeFormFields?.length; k++) {
			const transF = _.get(transQ, ["freeFormFields", k])
			const fMatch = _.get(qMatch, ["freeFormFields", k])
			if (fMatch) {
				_.entries(transF)
					.filter(([, v]) => typeof v === "string")
					.forEach(([k, v]) => {
						const key = k as "name" | "text"
						const str = fMatch[key]
						const translated = v
						if (str && translated) {
							_.set(dic, [str], translated)
						}
					})
			}
		}
	}

	return dic
}

/**
 * Return yup object schema for available translation languages.
 *
 * @param baseSchemaObj 					Argument of resulting yup.object()
 * @returns												Yup schema object with languageCode keys
 */
export const getTranslationsSchema = (baseSchemaObj: yup.ObjectShape) => {
	const schema = yup.object(
		TRANSLATION_CODES.reduce((a, b) => {
			_.set(a, b, yup.object(baseSchemaObj))
			return a
		}, {})
	)
	return schema
}

/**
 * Returns a default object with languageCode keys
 *
 * @param initialValue		Value for each language code attribute, defaults to empty object
 * @returns								Yup schema object with languageCode keys
 */
export const getTranslationsDefaultValue = (initialValue: Record<string, unknown>) => {
	return TRANSLATION_CODES.reduce((a, b) => {
		_.set(a, b, initialValue != null ? initialValue : {})
		return a
	}, {})
}

export const useGetLocRoleTitle = (language?: LanguageCode | string) => {
	const { isAuthenticated } = useSelector(authSelector)
	const { t, i18n } = useTranslation()
	const { data: templateRoleData } = useGetTemplateRolesQueryL({ noLoc: true }, { skip: !isAuthenticated }) // Result should automatically be translated to current language
	if (!language) {
		language = i18n.language as LanguageCode
	}
	return (defaultLangRoleTitle: string) => {
		const fallbackValue = !defaultLangRoleTitle ? "" : t(`values|roles~${defaultLangRoleTitle}`)
		if (!templateRoleData) {
			return fallbackValue
		}
		const tr = templateRoleData.find((tr) => tr.title === defaultLangRoleTitle)
		return loc(tr, language as LanguageCode)?.title ?? fallbackValue
	}
}
